import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import ActionSidePanel from 'src/components/design-system/ActionSidePanel';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import useConfirm from 'src/hooks/useConfirm';
import { ShoppingList } from 'src/types/view_shopping_lists/ShoppingList';
import ShoppingListForm from './ShoppingListForm';
import { useShoppingListForm } from './useShoppingListForm';

export interface EditShoppingListSidePanelProps {
  isOpen: boolean;
  shoppingList?: ShoppingList | null;
  onClose: () => void;
  onSaveSuccess?: () => void | Promise<void>;
}

const EditShoppingListSidePanel: FC<EditShoppingListSidePanelProps> = ({
  isOpen,
  shoppingList = null,
  onClose,
  onSaveSuccess,
}) => {
  const { t } = useTranslation();
  const { confirm } = useConfirm();

  const {
    values,
    errors,
    touched,
    isDirty,
    isValid,
    isSaving,
    setFieldValue,
    handleFieldBlur,
    handleSave,
  } = useShoppingListForm({
    mode: 'edit',
    isOpen,
    shoppingList,
    onSuccess: onSaveSuccess ?? onClose,
  });

  const confirmDiscardChanges = async (): Promise<boolean> => {
    if (!isDirty) {
      return true;
    }

    return confirm({
      title: t('Unsaved Changes'),
      content: t('You have unsaved changes. Do you want to discard them?'),
      cancelButtonText: t('Cancel'),
      confirmButtonText: t('Yes, Discard'),
      width: '500px',
    });
  };

  const handleClose = async () => {
    const shouldClose = await confirmDiscardChanges();
    if (shouldClose) {
      onClose();
    }
  };

  if (!shoppingList) {
    return null;
  }

  const saveDisabledTooltip = (
    <PopUpContentWrapper type="Information" contents={[t('No changes made')]} />
  );

  return (
    <ActionSidePanel
      isOpen={isOpen}
      onClose={handleClose}
      title={t('Edit List')}
      showTitleTooltip={false}
      onSave={handleSave}
      onCancel={handleClose}
      saveDisabled={!isValid || !isDirty}
      saveLoading={isSaving}
      cancelDisabled={isSaving}
      saveDisabledTooltip={saveDisabledTooltip}
      width="480px"
    >
      <ShoppingListForm
        values={values}
        errors={errors}
        touched={touched}
        onFieldChange={setFieldValue}
        onFieldBlur={handleFieldBlur}
      />
    </ActionSidePanel>
  );
};

export default EditShoppingListSidePanel;
